import { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import PropTypes from "prop-types";

import useLanguageChange from "../../../hooks/useLanguageChange";

import { GLOBAL_MEETING_DATA, PublicVariables } from "../../../constants/PublicVariables";
import {
    FILE_UPLOAD_LIMITATION,
    MAX_IMAGE_COUNT_OF_INDEXED_DB,
    PICKED_FILES_FROM,
    SUB_SCREEN,
    UPLOAD_FILES_DATA,
    UPLOAD_TYPE,
} from "../../../constants/UploadFilesConstants";
import { EVENT_NAME } from "../../../constants/GTagConstants";
import googleEvent from "../../../services/googleEvent";
import { changeIsFromMarkupValue, changeUploadFileSubScreen } from "../../../reducers/uploadFile-reducer";
import uploadFilesService from "../../../services/uploadFilesService";
import { handleEndCallFunc } from "../../Twilio/Room/roomFunction";
import { addDragHoverClass, removeDragHoverClass, stopPropagationAndPreventEvent } from "../../CommonModals/CommonUtils";
import { isOfflinePhotoCaptureFeatureAvailable } from "../../../shared/utility";
import OfflineSelectFiles from "../../OfflineMode/OfflineSelectFiles";

const SelectFiles = ({ updatedSelectedFiles, dispatchJoinMeeting }) => {
    const dispatch = useDispatch();
    const { isOfflineModeActive } = useSelector((state) => state.offlineMode);
    const { isFromMarkup } = useSelector((state) => state.uploadFile);

    const localisedValues = useLanguageChange();

    const dragDropDivRef = useRef();

    useEffect(() => {
        if (GLOBAL_MEETING_DATA.uploadFileAction === UPLOAD_TYPE.FROM_DEVICE) {
            document.getElementById("file-upload")?.click();
            GLOBAL_MEETING_DATA.uploadFileAction = UPLOAD_TYPE.EMPTY;
        } else if (GLOBAL_MEETING_DATA.uploadFileAction === UPLOAD_TYPE.FROM_CAMERA) {
            onFromCameraClickHandler();
            GLOBAL_MEETING_DATA.uploadFileAction = UPLOAD_TYPE.EMPTY;
        }
    }, []);

    const addDragClass = (e) => {
        stopPropagationAndPreventEvent(e);
        addDragHoverClass(dragDropDivRef);
    };

    const removeDragClass = (e) => {
        stopPropagationAndPreventEvent(e);
        removeDragHoverClass(dragDropDivRef);
    };

    const dropHandler = (e) => {
        removeDragClass(e);

        const allFiles = Array.from(e.dataTransfer.files);

        googleEvent.logEvent(EVENT_NAME.UPLOAD_FILE_DRAG_DROP);
        UPLOAD_FILES_DATA.pickedFilesFrom = PICKED_FILES_FROM.FROM_GALLERY;
        if (allFiles.length > 0) uploadFilesService.generateFilesArray(allFiles, updatedSelectedFiles);
    };

    const dragLeaveHandler = (e) => {
        removeDragClass(e);
    };

    const dragEndHandler = (e) => {
        removeDragClass(e);
    };

    const dragOverHandler = (e) => {
        addDragClass(e);
    };

    const dragChange = (e, pickedFilesFrom = PICKED_FILES_FROM.NONE) => {
        stopPropagationAndPreventEvent(e);
        const allFiles = Array.from(e.target.files);

        UPLOAD_FILES_DATA.pickedFilesFrom = pickedFilesFrom;
        dispatch(changeIsFromMarkupValue(false));

        if (allFiles.length > 0) uploadFilesService.generateFilesArray(allFiles, updatedSelectedFiles);
    };

    const inputClick = (e) => {
        e.target.value = null;
        if (!isFromMarkup) {
            googleEvent.logEvent(EVENT_NAME.UPLOAD_FILE_FROM_DEVICE_TAPPED);
        }

        dispatch(changeIsFromMarkupValue(false));
    };

    const onFromCameraClickHandler = () => {
        googleEvent.logEvent(EVENT_NAME.UPLOAD_FILE_FROM_CAMERA_TAPPED);
        if (PublicVariables.isMobile) {
            /* add mobile evnt handler code here */
            if (isOfflineModeActive) {
                document.getElementById("cameraFileInput_selectFiles_for_offline_mode")?.click();
            } else {
                document.getElementById("cameraFileInput_selectFiles")?.click();
            }
        } else {
            dispatch(changeUploadFileSubScreen(SUB_SCREEN.CAMERA_VIEW));
        }
    };

    const reConnectButton = () => {
        dispatchJoinMeeting();
    };

    return (
        <div className='upload-files-in custom-scroll' data-testid='uploadFilesView'>
            <div className='upload-files-box-wrap'>
                <div className='upload-files-box-out'>
                    <div className='upload-files-box'>
                        <div className='upload-files-head'>
                            <div className='head-text'>{localisedValues.upload_files}</div>
                        </div>
                        {!isOfflineModeActive && (
                            <div className='drag-file-upload'>
                                <div
                                    id='image_drag_drop_div'
                                    ref={dragDropDivRef}
                                    className='drag-file-upload-in'
                                    onDrop={dropHandler}
                                    onDragLeave={dragLeaveHandler}
                                    onDragEnd={dragEndHandler}
                                    onDragOver={dragOverHandler}
                                >
                                    <label className='drag-file-upload-label' htmlFor='file-upload'>
                                        <div className='upload-icon'>
                                            <span className='icon-upload-cloud'></span>
                                        </div>
                                        {!PublicVariables.isMobile && (
                                            <div className='drophere-text'>{localisedValues.drop_for_upload_or_select}</div>
                                        )}
                                        <div className='button-inline button-inline-tb-space d-flex justify-content-center'>
                                            <div className='button'>
                                                <button
                                                    className='btn btn-primary btn-with-icon drag-upload-files-btn'
                                                    onClick={() => {
                                                        document.getElementById("file-upload").click();
                                                    }}
                                                >
                                                    <span className='btn-icon icon-upload-device'></span>
                                                    <span className='btn-text'>{localisedValues.from_device}</span>
                                                </button>
                                            </div>
                                            <div className='button'>
                                                <button
                                                    className='btn btn-primary btn-with-icon drag-upload-files-btn'
                                                    onClick={() => onFromCameraClickHandler()}
                                                >
                                                    <span className='btn-icon icon-camera'></span>
                                                    <span className='btn-text'>{localisedValues.from_camera}</span>
                                                    <input
                                                        className='d-none'
                                                        id='cameraFileInput_selectFiles'
                                                        type='file'
                                                        accept='image/*'
                                                        capture='camera'
                                                        onClick={(e) => {
                                                            e.target.value = null;
                                                        }}
                                                        onChange={(e) => dragChange(e, PICKED_FILES_FROM.FROM_CAMERA)}
                                                    />
                                                </button>
                                            </div>
                                        </div>
                                        <input
                                            className='drag-file-upload-input'
                                            id='file-upload'
                                            type='file'
                                            title=''
                                            accept='image/png,image/jpeg,image/jpg'
                                            multiple
                                            onChange={(e) => dragChange(e, PICKED_FILES_FROM.FROM_GALLERY)}
                                            onClick={(e) => inputClick(e)}
                                        />
                                    </label>
                                </div>
                            </div>
                        )}
                        {isOfflineModeActive && (
                            <OfflineSelectFiles
                                dragChange={dragChange}
                                onFromCameraClickHandler={onFromCameraClickHandler}
                                localisedValues={localisedValues}
                            />
                        )}
                        {!isOfflineModeActive && (
                            <div className='upload-files-bottom'>
                                {GLOBAL_MEETING_DATA.remainingFileCount > FILE_UPLOAD_LIMITATION.SINGLE_UPLOAD_MAX_ALLOWED_FILE_COUNT && (
                                    <p>{localisedValues?.upload_20_file}</p>
                                )}
                                {GLOBAL_MEETING_DATA.remainingFileCount < FILE_UPLOAD_LIMITATION.SINGLE_UPLOAD_LIMIT_REACH_COUNT && (
                                    <p>{localisedValues?.upload_max_count?.replace("<remainingCount>", GLOBAL_MEETING_DATA.remainingFileCount)}</p>
                                )}
                                <p>{localisedValues?.max_size_20MB?.replace("<fileSize>", uploadFilesService.getMaxAllowedFileSize())}</p>
                            </div>
                        )}
                        {isOfflineModeActive && (
                            <div className='upload-files-bottom'>
                                <p>{localisedValues?.max_images_upload.replace("<count>", MAX_IMAGE_COUNT_OF_INDEXED_DB)}</p>
                            </div>
                        )}
                        {/* For upload only case */}
                        {PublicVariables.isOnlyUploadImageMeet && (
                            <div className='cancel-upload-link'>
                                <div className='button'>
                                    <button
                                        className='btn btn-link'
                                        onClick={() => {
                                            handleEndCallFunc();
                                        }}
                                    >
                                        <span className='btn-text'>{localisedValues.cancel_upload}</span>
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
                {isOfflinePhotoCaptureFeatureAvailable() && isOfflineModeActive && (
                    <div className='offline-mode-bottom'>
                        <div className='button-inline button-inline-tb-space d-flex justify-content-center'>
                            <div className='button'>
                                <button className='btn btn-action' onClick={reConnectButton}>
                                    {localisedValues?.reconnect}
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

SelectFiles.propTypes = {
    updatedSelectedFiles: PropTypes.func,
};

export default SelectFiles;
